import { ACCENTS } from "./accents.js";
import { STEPS } from "./guide.js";
import type { Audience, DemoState } from "./types.js";

export interface DemoStore {
  readonly persistent: boolean;
  load(): Promise<DemoState | null>;
  save(state: DemoState): Promise<void>;
  clear(): Promise<void>;
}

const DATABASE = "kuori-demo-v2";
const TABLE = "state";
const KEY = "current";

export function initialState(now: number, audience: Audience = "planner"): DemoState {
  return {
    audience,
    brand: null,
    guest: null,
    photos: [],
    hidden: [],
    guide: { step: "brand", collapsed: false },
    tipSeen: false,
    startedAt: now,
  };
}

type Loose = Record<string, unknown>;

function isRecord(value: unknown): value is Loose {
  return typeof value === "object" && value !== null;
}

function isImage(value: unknown): boolean {
  return (
    isRecord(value) &&
    Object.prototype.toString.call(value.bytes) === "[object ArrayBuffer]" &&
    typeof value.type === "string"
  );
}

function isPhoto(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.author === "string" &&
    typeof value.at === "number" &&
    typeof value.width === "number" &&
    typeof value.height === "number" &&
    isImage(value.image)
  );
}

function isBrand(value: unknown): boolean {
  return (
    value === null ||
    (isRecord(value) &&
      typeof value.name === "string" &&
      typeof value.eventName === "string" &&
      ACCENTS.some((option) => option.id === value.accent) &&
      (value.logo === null || isImage(value.logo)))
  );
}

function isGuest(value: unknown): boolean {
  return (
    value === null ||
    (isRecord(value) && typeof value.first === "string" && typeof value.last === "string")
  );
}

function isState(value: unknown): value is DemoState {
  if (!isRecord(value) || !isRecord(value.guide)) {
    return false;
  }
  const guide = value.guide;
  return (
    (value.audience === "planner" || value.audience === "client") &&
    STEPS.some((copy) => copy.step === guide.step) &&
    typeof guide.collapsed === "boolean" &&
    Array.isArray(value.photos) &&
    value.photos.every(isPhoto) &&
    Array.isArray(value.hidden) &&
    value.hidden.every((id) => typeof id === "string") &&
    typeof value.tipSeen === "boolean" &&
    typeof value.startedAt === "number" &&
    isBrand(value.brand) &&
    isGuest(value.guest)
  );
}

export function memoryStore(): DemoStore {
  let saved: DemoState | null = null;
  return {
    persistent: false,
    load: async () => saved,
    save: async (state) => {
      saved = state;
    },
    clear: async () => {
      saved = null;
    },
  };
}

function settle<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  const opening = factory.open(DATABASE, 1);
  opening.onupgradeneeded = () => {
    opening.result.createObjectStore(TABLE);
  };
  return settle(opening);
}

export async function openStore(
  factory: IDBFactory | undefined = globalThis.indexedDB,
): Promise<DemoStore> {
  if (!factory) {
    return memoryStore();
  }
  try {
    const database = await openDatabase(factory);
    const run = <T>(mode: IDBTransactionMode, work: (table: IDBObjectStore) => IDBRequest<T>) =>
      settle(work(database.transaction(TABLE, mode).objectStore(TABLE)));
    return {
      persistent: true,
      load: async () => {
        const value = await run("readonly", (table) => table.get(KEY));
        return isState(value) ? value : null;
      },
      save: async (state) => {
        await run("readwrite", (table) => table.put(state, KEY));
      },
      clear: async () => {
        await run("readwrite", (table) => table.delete(KEY));
      },
    };
  } catch {
    return memoryStore();
  }
}
